// src/services/userService.js
import apiClient from "./api";

const API = "api";

// Reusable helper function (consider moving to a shared utils file)
const mapTableParamsToApi = (params = {}) => {
  const apiParams = {};
  if (params.pagination) {
    apiParams.page = params.pagination.current;
    apiParams.pageSize = params.pagination.pageSize;
  }
  if (params.filters) {
    // Add filter mapping if needed (e.g., by role)
    if (params.filters.role && params.filters.role.length > 0) {
      apiParams.role = params.filters.role[0];
    }
  }
  if (params.sorter && params.sorter.field) {
    apiParams.sortField = params.sorter.field;
    apiParams.sortOrder = params.sorter.order === "ascend" ? "asc" : "desc";
  }
  if (params.search) {
    apiParams.search = params.search;
  }
  return apiParams;
};

/**
 * Fetches users from the backend.
 * @param {object} params - Parameters from UI for pagination, sorting, filtering.
 * @returns {Promise<{data: Array<object>, total: number, ...}>} Paginated user data.
 * @throws {Error} If fetching fails.
 */
export const getUsers = async (params = {}) => {
  try {
    const apiParams = mapTableParamsToApi(params);
    const response = await apiClient.get(`${API}/users`, { params: apiParams });
    return response.data;
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch users.";
    console.error("getUsers service error:", message);
    throw new Error(message);
  }
};

/**
 * Adds a new user (typically by an admin).
 * @param {object} userData - { name, email, password, role }.
 * @returns {Promise<object>} The newly created user object (without password).
 * @throws {Error} If creation fails.
 */
export const addUser = async (userData) => {
  try {
    const response = await apiClient.post(`${API}/users`, userData);
    return response.data;
  } catch (error) {
    const message =
      error.response?.data?.message || error.message || "Failed to add user.";
    console.error("addUser service error:", message);
    // Handle specific validation errors if backend returns them
    if (error.response?.data?.errors) {
      const validationErrors = error.response.data.errors
        .map((e) => e.msg)
        .join(", ");
      console.error("Add user validation errors:", validationErrors);
      throw new Error(`Add user failed: ${validationErrors}`);
    }
    throw new Error(message);
  }
};

/**
 * Updates an existing user.
 * @param {string} userId - The ID of the user to update.
 * @param {object} updatedData - Fields to update { name?, email?, password?, role? }.
 * @returns {Promise<object>} The updated user object (without password).
 * @throws {Error} If update fails.
 */
export const updateUser = async (userId, updatedData) => {
  try {
    // IMPORTANT: Prevent sending empty password field if not changing password
    const dataToSend = { ...updatedData };
    if (!dataToSend.password) {
      delete dataToSend.password;
    }
    // Ensure ID is not sent in body
    delete dataToSend._id;
    delete dataToSend.id;

    const response = await apiClient.put(`${API}/users/${userId}`, dataToSend);
    return response.data;
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      "Failed to update user.";
    console.error("updateUser service error:", message);
    if (error.response?.data?.errors) {
      const validationErrors = error.response.data.errors
        .map((e) => e.msg)
        .join(", ");
      console.error("Update user validation errors:", validationErrors);
      throw new Error(`Update user failed: ${validationErrors}`);
    }
    throw new Error(message);
  }
};

/**
 * Deletes a user.
 * @param {string} userId - The ID of the user to delete.
 * @returns {Promise<{success: boolean, id: string}>} Confirmation object.
 * @throws {Error} If deletion fails.
 */
export const deleteUser = async (userId) => {
  try {
    const response = await apiClient.delete(`${API}/users/${userId}`);
    return response.data;
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      "Failed to delete user.";
    console.error("deleteUser service error:", message);
    throw new Error(message);
  }
};
